//
// # Layout pin geometry
//
// A `LayoutPin` is its `shapes`; it has no point or layer of its own. These
// answer a one-shape pin's point and layer, and refuse, naming the pin, for a
// pin with several: such a caller reads `shapes`. Rust's
// `LayoutPin::loc`, `LayoutPin::layer` and `LayoutPinShape::pad` are the same
// rules, so both sides agree on every pin.
//

import { Result, Ok, Err } from "CoreUtils";
import type { Bbox, Point } from "./core";
import type { LayoutPin, LayoutPinShape, PinShape } from "./layout";
import type { MetalLayer } from "./metal";

/** The pin's Terminal: `terminal`, or else the pin's name. */
export function pinTerminal(pin: Pick<LayoutPin, "name" | "terminal">): string {
  return pin.terminal ?? pin.name;
}

/** A pin asked for its point or layer when it has several shapes. */
export interface PinShapeCountError {
  /** The pin's name. */
  pin: string;
  /** How many shapes it has. */
  shapes: number;
  message: string;
}

/** The pin's only shape. `Err` names the pin when it has several. */
export function pinSoleShape(
  pin: LayoutPin
): Result<LayoutPinShape, PinShapeCountError> {
  const shapes = pin.shapes;
  if (shapes.length === 1) return Ok(shapes[0]);
  return Err({
    pin: pin.name,
    shapes: shapes.length,
    message: `layout pin "${pin.name}" has ${shapes.length} shapes; a point and a layer are defined only for a one-shape pin`,
  });
}

/**
 * The centre of the pin's only shape: the centre of its bounding box, rounded
 * down to the integer grid; a point shape's point. `Err` names the pin when it
 * has several shapes.
 */
export function pinLoc(pin: LayoutPin): Result<Point, PinShapeCountError> {
  return pinSoleShape(pin).map((s) => pinShapeCenter(s.shape));
}

/** The layer of the pin's only shape. `Err` names the pin when it has several shapes. */
export function pinLayer(pin: LayoutPin): Result<number, PinShapeCountError> {
  return pinSoleShape(pin).map((s) => s.layer);
}

/**
 * A box covering the shape; a point's is empty, at the point. A path's grows its points' box by its half width
 * plus its larger end extension on every side, so it may be larger than the metal.
 */
export function pinShapeBbox(shape: PinShape): Bbox {
  switch (shape.kind) {
    case "rect":
      return shape.bbox;
    case "polygon":
      return pointsBbox(shape.points, 0);
    case "path":
      return pointsBbox(
        shape.points,
        Math.trunc(shape.style.width / 2) +
          Math.max(shape.style.beginExt, shape.style.endExt)
      );
    case "point":
      return { min: shape.at, max: shape.at };
  }
}

/** The centre of the shape's bounding box, rounded down to the integer grid. */
export function pinShapeCenter(shape: PinShape): Point {
  const b = pinShapeBbox(shape);
  return {
    x: Math.floor((b.min.x + b.max.x) / 2),
    y: Math.floor((b.min.y + b.max.y) / 2),
  };
}

/**
 * A point on `layer`/`purpose`: no area, for a writer that knows only a point.
 * The pin rules flag it as having no metal.
 */
export function pinPoint(
  layer: number,
  purpose: number,
  at: Point
): LayoutPinShape {
  return { layer, purpose, shape: { kind: "point", at } };
}

/**
 * A pin pad: a square of `metal`'s route width centred on `at`, on its own
 * layer and purpose: the metal a generator draws for a pin.
 */
export function pinPad(metal: MetalLayer, at: Point): LayoutPinShape {
  return pinSquare(metal.layer, metal.purpose, metal.width, at);
}

/**
 * A `width` square on `layer`/`purpose` centred on `at`. Odd widths put the
 * extra unit on the high side, so `pinShapeCenter` gives back `at`.
 */
export function pinSquare(
  layer: number,
  purpose: number,
  width: number,
  at: Point
): LayoutPinShape {
  const lo = Math.floor(width / 2);
  const hi = width - lo;
  return {
    layer,
    purpose,
    shape: {
      kind: "rect",
      bbox: {
        min: { x: at.x - lo, y: at.y - lo },
        max: { x: at.x + hi, y: at.y + hi },
      },
    },
  };
}

/** A pin whose Terminal is its name, joined to nothing else in the cell. */
export function layoutPin(
  name: string,
  first: LayoutPinShape,
  ...rest: LayoutPinShape[]
): LayoutPin {
  return {
    name,
    terminal: name,
    internalConnection: "none",
    shapes: [first, ...rest],
  };
}

function pointsBbox(points: readonly Point[], grow: number): Bbox {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  return {
    min: { x: Math.min(...xs) - grow, y: Math.min(...ys) - grow },
    max: { x: Math.max(...xs) + grow, y: Math.max(...ys) + grow },
  };
}
